import React from "react";

import Alert from "@mui/material/Alert";
import CircularProgress from "@mui/material/CircularProgress";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import CloseIcon from "@mui/icons-material/Close";
import RefreshIcon from "@mui/icons-material/Refresh";
import ReplayIcon from "@mui/icons-material/Replay";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import VisibilityIcon from "@mui/icons-material/Visibility";

import { ListComponent } from "@auto/widgets/ListComponent";
import { getLocale, rest, t } from "@auto/services";
import { getServiceUri } from "@auto/metadata";
import { can } from "@src/security/permissions";
import {
  buildApplicantName,
  formatStatusCode,
  renderOniPortrait,
  renderPortrait,
  renderTags,
} from "@src/lists/VoterApplicationList";
import { VoterApplicationListFacetButton } from "@src/lists/VoterApplicationListFacets";

import facetStyles from "@src/lists/VoterApplicationListFacets.module.styl";
import styles from "./MaintenanceApplicationsPage.module.styl";

const STATUS_ERROR = 2;
const STATUS_MANUAL_CHECK = 4;
const PAGE_SIZE = 50;

const MaintenanceContext = React.createContext(null);

const maintenanceRequest = (path, method = "GET", body) =>
  rest.request(`${getServiceUri()}maintenance/voter-applications/${path}`, method, body);

const formatDateTime = (value) => {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return new Intl.DateTimeFormat(getLocale(), {
    dateStyle: "short", timeStyle: "short",
  }).format(date);
};

const formatCount = (count) => new Intl.NumberFormat(getLocale()).format(count || 0);
const withCount = (key, count) => t(key).replace("{count}", formatCount(count));

const STATUS_OPTIONS = [
  { value: null, label: "maintenance.allExceptions", countField: "total" },
  { value: STATUS_ERROR, label: "common.error", countField: "errorCount" },
  { value: STATUS_MANUAL_CHECK, label: "maintenance.manualCheck", countField: "manualCheckCount" },
];

const normalizeMaintenanceFilters = (filters) => ({
  statusCode: filters?.statusCode === STATUS_ERROR || filters?.statusCode === STATUS_MANUAL_CHECK
    ? filters.statusCode : null,
});

const MaintenanceFiltersDrawer = ({ controller }) => {
  const { summary } = React.useContext(MaintenanceContext);
  const { statusCode } = normalizeMaintenanceFilters(controller.value);

  return (
    <div className={facetStyles.drawer}>
      <div className={facetStyles.drawerHeader}>
        <div className={facetStyles.drawerTitle}>{t("common.filters")}</div>
        <button type="button" className={facetStyles.closeButton}
          onClick={controller.closeDrawer} aria-label={t("common.closeFilters")}>
          <CloseIcon fontSize="inherit" />
        </button>
      </div>
      <div className={facetStyles.drawerBody}>
        <section className={facetStyles.section}>
          <div className={facetStyles.sectionTitle}>{t("common.status")}</div>
          <div className={facetStyles.sectionBody}>
            {STATUS_OPTIONS.map(({ value, label, countField }) => (
              <button key={value ?? "all"} type="button"
                className={statusCode === value
                  ? `${facetStyles.option} ${facetStyles.optionSelected}`
                  : facetStyles.option}
                aria-pressed={statusCode === value}
                onClick={() => controller.setValue({ statusCode: value })}>
                <span className={facetStyles.radioDot} aria-hidden="true" />
                <span className={facetStyles.optionLabel}>{t(label)}</span>
                <span className={facetStyles.count}>{formatCount(summary[countField])}</span>
              </button>
            ))}
          </div>
        </section>
      </div>
      <div className={facetStyles.drawerFooter}>
        <button type="button" className={facetStyles.secondaryAction}
          onClick={controller.reset} disabled={controller.activeCount === 0}>
          {t("common.resetFilters")}
        </button>
        <button type="button" className={facetStyles.primaryAction}
          onClick={controller.closeDrawer}>{t("common.done")}</button>
      </div>
    </div>
  );
};

// Use exactly the shared ListComponent facet system used by the other inscription lists.
// Keeping this object stable also avoids re-fetching on selection/summary changes.
const MAINTENANCE_FACETS = {
  storageKey: "maintenance.voter-applications.status",
  overlayLabel: t("common.filters"),
  defaultValue: { statusCode: null },
  normalizeValue: normalizeMaintenanceFilters,
  isDefaultValue: (value) => normalizeMaintenanceFilters(value).statusCode === null,
  getActiveCount: (value) => normalizeMaintenanceFilters(value).statusCode === null ? 0 : 1,
  renderButton: (controller) => <VoterApplicationListFacetButton controller={controller} />,
  renderDrawer: (controller) => <MaintenanceFiltersDrawer controller={controller} />,
};

const getMaintenanceRowId = (row) => row.gid;
const isMaintenanceRowSelectable = (row) => row.statusCode === STATUS_MANUAL_CHECK;
const getMaintenanceRowLabel = (row) =>
  `${t("maintenance.selectApplication")} ${row.gid}`;

const MaintenanceRowActions = ({ row, gui }) => {
  const { canReprocess, canViewApplication, processing, askReprocess } =
    React.useContext(MaintenanceContext);
  return (
    <>
      {canViewApplication && (
        <Tooltip title={t("voter.viewApplication")}>
          <IconButton size="small" onClick={() => gui.goTo(`/voter-application/${row.gid}`)}>
            <VisibilityIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      )}
      {canReprocess && row.statusCode === STATUS_MANUAL_CHECK && (
        <Tooltip title={t("maintenance.reprocess")}>
          <span>
            <IconButton size="small" disabled={processing}
              onClick={() => askReprocess("selected", [row.gid])}>
              <ReplayIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
      )}
    </>
  );
};

const MaintenanceActionBar = ({ facetController }) => {
  const {
    summary, selected, processing, canReprocess,
    summaryError, loadError, actionError, success,
    askReprocess, refresh,
  } = React.useContext(MaintenanceContext);
  const [actionAnchor, setActionAnchor] = React.useState(null);
  const statusCode = normalizeMaintenanceFilters(facetController.value).statusCode;
  const selectedStatus = STATUS_OPTIONS.find((option) => option.value === statusCode);
  const hasReprocessable = selected.size > 0 || summary.manualCheckCount > 0;

  const requestReprocess = (scope, gids) => {
    setActionAnchor(null);
    askReprocess(scope, gids);
  };

  return (
    <div className={styles.maintenanceBar}>
      <div className={styles.actionsBar}>
        {statusCode !== null ? (
          <button type="button" className={facetStyles.chip}
            onClick={facetController.reset}>
            {t("common.status")}: {t(selectedStatus.label)}
            <span className={facetStyles.chipClose} aria-hidden="true">×</span>
          </button>
        ) : (
          <div className={styles.counts} aria-live="polite">
            <span className={styles.stat}>
              <span className={styles.statLabel}>{t("common.error")}</span>
              <strong className={styles.statValue}>{formatCount(summary.errorCount)}</strong>
            </span>
            <span className={styles.countDivider} aria-hidden="true" />
            <span className={styles.stat}>
              <span className={styles.statLabel}>{t("maintenance.manualCheck")}</span>
              <strong className={styles.statValue}>{formatCount(summary.manualCheckCount)}</strong>
            </span>
          </div>
        )}

        <div className={styles.toolbarActions}>
          {canReprocess ? (
            <>
              <button type="button"
                className={styles.actionsButton}
                aria-haspopup="menu"
                aria-controls={actionAnchor ? "maintenance-reprocess-menu" : undefined}
                aria-expanded={Boolean(actionAnchor)}
                disabled={processing || !hasReprocessable}
                onClick={(event) => setActionAnchor(event.currentTarget)}>
                <ReplayIcon fontSize="small" />
                <span>{t("common.actions")}</span>
                {selected.size ? (
                  <span className={styles.selectedCount}>{formatCount(selected.size)}</span>
                ) : null}
                <ExpandMoreIcon className={styles.chevron} fontSize="small" />
              </button>
              <Menu
                id="maintenance-reprocess-menu"
                anchorEl={actionAnchor}
                open={Boolean(actionAnchor)}
                onClose={() => setActionAnchor(null)}
                anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                transformOrigin={{ vertical: "top", horizontal: "right" }}
                PaperProps={{ className: styles.actionsMenuPaper }}>
                <MenuItem className={styles.actionMenuItem}
                  disabled={processing || selected.size === 0}
                  onClick={() => requestReprocess("selected", [...selected])}>
                  <ReplayIcon fontSize="small" />
                  {withCount("maintenance.reprocessSelected", selected.size)}
                </MenuItem>
                <MenuItem className={styles.actionMenuItem}
                  disabled={processing || summary.manualCheckCount === 0}
                  onClick={() => requestReprocess("all")}>
                  <ReplayIcon fontSize="small" />
                  {t("maintenance.reprocessAll")}
                  <span className={styles.actionCount}>{formatCount(summary.manualCheckCount)}</span>
                </MenuItem>
              </Menu>
            </>
          ) : null}
          <button type="button" className={styles.refreshButton}
            onClick={refresh} disabled={processing} title={t("maintenance.refresh")}
            aria-label={t("maintenance.refresh")}>
            <RefreshIcon fontSize="small" />
          </button>
        </div>
      </div>
      {(summaryError || loadError || actionError || success) && (
        <div className={styles.messages}>
          {summaryError ? <Alert severity="error">{summaryError}</Alert> : null}
          {loadError ? <Alert severity="error">{loadError}</Alert> : null}
          {actionError ? <Alert severity="error">{actionError}</Alert> : null}
          {success ? <Alert severity="success">{success}</Alert> : null}
        </div>
      )}
    </div>
  );
};

const renderMaintenanceActionsBar = ({ facetController }) => (
  <MaintenanceActionBar facetController={facetController} />
);
const renderMaintenanceActions = (row, gui) => <MaintenanceRowActions row={row} gui={gui} />;
const columns = [
  { title: "voter.oniPhoto", field: "oniPhotoId", sorting: false, filtering: false, render: renderOniPortrait },
  { title: "voter.applicationPhoto", field: "id", sorting: false, filtering: false, render: renderPortrait },
  { title: "voter.applicant", field: "applicantName", sorting: false },
  { title: "person.nationalId", field: "nationalIdentifier", sorting: false },
  { title: "voter.receiptNumber", field: "receiptNumber", sorting: false },
  { title: "common.status", field: "statusCodeFormatted", sorting: false },
  { title: "voter.submittedAt", field: "submittedAtFormatted", sorting: false },
  { title: "common.tags", field: "tags", sorting: false, render: renderTags },
  { title: "common.actions", field: "__actions", sorting: false, filtering: false },
];

const MaintenanceApplicationsPage = ({ gui }) => {
  const [summary, setSummary] = React.useState({ errorCount: 0, manualCheckCount: 0, total: 0 });
  const [selected, setSelected] = React.useState(() => new Set());
  const [refreshToken, setRefreshToken] = React.useState(0);
  const [summaryError, setSummaryError] = React.useState("");
  const [loadError, setLoadError] = React.useState("");
  const [actionError, setActionError] = React.useState("");
  const [success, setSuccess] = React.useState("");
  const [confirmAction, setConfirmAction] = React.useState(null);
  const [processing, setProcessing] = React.useState(false);
  const rowRequestId = React.useRef(0);

  const canReprocess = can(gui, "REPROCESS_VOTER_APPLICATION");
  const canViewApplication = can(gui, "READ_VOTER_APPLICATION");

  React.useEffect(() => {
    let active = true;
    maintenanceRequest("summary").then((result) => {
      if (active) {
        setSummary(result);
        setSummaryError("");
      }
    }).catch((failure) => {
      if (active) setSummaryError(failure?.message || t("maintenance.loadFailed"));
    });
    return () => { active = false; };
  }, [refreshToken]);

  const refresh = () => setRefreshToken((value) => value + 1);

  const askReprocess = (scope, gids = []) => {
    if (processing) return;
    const count = scope === "all" ? summary.manualCheckCount : gids.length;
    if (count <= 0) return;
    setConfirmAction({ scope, gids, count });
    setSuccess("");
    setActionError("");
  };

  const performAction = async () => {
    if (!confirmAction || processing) return;
    setProcessing(true);
    setActionError("");
    try {
      const { scope, gids } = confirmAction;
      const result = await maintenanceRequest("reprocess", "POST",
        scope === "all" ? { scope } : { scope, gids });
      setConfirmAction(null);
      setSelected(new Set());
      setSuccess(withCount("maintenance.reprocessedCount", result.reprocessed));
      refresh();
    } catch (failure) {
      setActionError(failure?.message || t("maintenance.actionFailed"));
      setConfirmAction(null);
    } finally {
      setProcessing(false);
    }
  };

  const loadRows = React.useCallback(async (query) => {
    const requestId = ++rowRequestId.current;
    try {
      const result = await maintenanceRequest("search", "POST", {
        statusCode: normalizeMaintenanceFilters(query.filters).statusCode,
        search: query.search,
        limit: query.pageSize || PAGE_SIZE,
        offset: (query.page || 0) * PAGE_SIZE,
      });
      const rows = (result.rows || []).map((row) => ({
        ...row,
        applicantName: buildApplicantName(row),
        statusCodeFormatted: formatStatusCode(row.statusCode),
        submittedAtFormatted: formatDateTime(row.submittedAt),
      }));
      if (requestId === rowRequestId.current) {
        setSelected((current) => current.size ? new Set() : current);
        setLoadError("");
      }
      return { data: rows, totalCount: result.total, page: query.page || 0 };
    } catch (failure) {
      if (requestId === rowRequestId.current) {
        setLoadError(failure?.message || t("maintenance.loadFailed"));
        setSelected((current) => current.size ? new Set() : current);
      }
      return { data: [], totalCount: 0, page: query.page || 0 };
    }
  }, [refreshToken]);

  const selection = React.useMemo(() => canReprocess ? {
    selectedKeys: selected,
    getRowId: getMaintenanceRowId,
    isSelectable: isMaintenanceRowSelectable,
    onSelectionChange: setSelected,
    disabled: processing,
    selectPageLabel: t("maintenance.selectPage"),
    getRowLabel: getMaintenanceRowLabel,
  } : null, [canReprocess, selected, processing]);

  // A selection change does not change columns or fetch, so it cannot restart search.
  const projection = React.useMemo(() => ({
    storageKey: "maintenance.voter-applications",
    facets: MAINTENANCE_FACETS,
    activeFiltersControl: renderMaintenanceActionsBar,
    columns,
    selection,
    actions: renderMaintenanceActions,
    fetch: loadRows,
  }), [selection, loadRows]);

  return (
    <MaintenanceContext.Provider value={{
      summary, selected, processing,
      canReprocess, canViewApplication, summaryError, loadError, actionError, success,
      askReprocess, refresh,
    }}>
      <ListComponent gui={gui} projection={projection} />
      <Dialog open={Boolean(confirmAction)}
        onClose={processing ? undefined : () => setConfirmAction(null)} maxWidth="sm" fullWidth>
        <DialogTitle>{t("maintenance.confirmTitle")}</DialogTitle>
        <DialogContent>
          <p>{confirmAction?.scope === "all"
            ? withCount("maintenance.confirmAll", confirmAction.count)
            : withCount("maintenance.confirmSelected", confirmAction?.count ?? 0)}</p>
          <p className={styles.confirmExplanation}>{t("maintenance.confirmExplanation")}</p>
        </DialogContent>
        <DialogActions>
          <button type="button" className={facetStyles.filterButton}
            onClick={() => setConfirmAction(null)} disabled={processing}>{t("common.cancel")}</button>
          <button type="button" className={`${facetStyles.primaryAction} ${styles.dialogPrimary}`}
            onClick={performAction} disabled={processing}>
            {processing ? <CircularProgress size={16} /> : <ReplayIcon fontSize="small" />}
            {t("maintenance.reprocess")}
          </button>
        </DialogActions>
      </Dialog>
    </MaintenanceContext.Provider>
  );
};

export const displayMaintenanceApplications = (gui) => ({
  uuid: "maintenance-applications",
  view: () => <MaintenanceApplicationsPage gui={gui} />,
});

export default MaintenanceApplicationsPage;
